import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { hasPlan, planOf, PLANS } from "@/lib/plans";

const moduleNum = (id) => parseInt(String(id).replace(/\D/g, ""), 10) || 0;

const buildSummary = (stats, usersRaw, dist, exams) => {
  const users = Array.isArray(usersRaw) ? usersRaw : usersRaw?.users ?? [];
  const status = { approved: 0, pending: 0, blocked: 0, rejected: 0 };
  users.forEach((u) => {
    if (u.status in status) status[u.status] += 1;
  });

  const hasPlanData = users.some(hasPlan);
  const plans = PLANS.map((p) => ({ ...p, count: users.filter((u) => planOf(u) === p.key).length }));

  const modules = (dist?.modules ?? [])
    .map((m) => ({ id: m.id, name: m.name, count: m.count, submodules: m.submodules?.length ?? 0 }))
    .sort((a, b) => moduleNum(a.id) - moduleNum(b.id));

  const upcoming = [...(exams?.upcoming ?? [])].sort((a, b) => (a.exam_date > b.exam_date ? 1 : -1));
  const examIds = new Set([...(exams?.exams ?? []), ...upcoming, ...(exams?.past ?? [])].map((e) => e.id));

  const recentUsers = [...users]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);

  return {
    users: { total: users.length, ...status, admins: users.filter((u) => u.role === "admin").length },
    plans,
    hasPlanData,
    questions: { total: dist?.total_questions ?? stats.total_questions, modules },
    sessions: {
      total: stats.total_sessions ?? 0,
      completed: stats.completed_sessions ?? 0,
      answered: stats.questions_answered ?? 0,
      correct: stats.correct_answers ?? 0,
    },
    exams: { total: examIds.size, upcoming, pastCount: exams?.past?.length ?? 0 },
    recentUsers,
  };
};

const fetchDashboard = async () => {
  const [stats, users, dist, exams] = await Promise.all([
    api.get("/stats"),
    api.get("/admin/users"),
    api.get("/admin/questions/distribution"),
    api.get("/admin/exams"),
  ]);
  return buildSummary(stats.data, users.data, dist.data, exams.data);
};

export const useDashboard = () =>
  useQuery({ queryKey: ["dashboard"], queryFn: fetchDashboard, refetchInterval: 30000, staleTime: 0 });
